import React, { FC, useEffect, useState } from 'react'
import clsx from 'clsx'
import { useField } from 'formik'
import isEmpty from 'lodash.isempty'

import { ReactComponent as TooltipInfo } from 'assets/images/mini_tooltip.svg'

import { CloseCircleFilled } from '@ant-design/icons'

import { Input, Tooltip } from '@pepper/ui'

import styles from './_.module.css'

type OptionSet = {
  value: string | number
  label: string
}

interface Props {
  placeHolder: string
  name: string
  label?: React.ReactNode | string
  className?: string
  required?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: OptionSet[]
  defaultStaticValue: string | number
  dataType: string
  miniTooltip?: string
  disabled?: boolean
}

export const PillsSelector: FC<Props> = ({
  className = '',
  label,
  name,
  required,
  options,
  placeHolder,
  dataType,
  miniTooltip,
  disabled = false,
}) => {
  const [pillSet, setPillSet] = useState<OptionSet[]>([])
  const [field, meta, helpers] = useField(name)
  const [showCustomField, setToggleCustomField] = useState(() => {
    if (
      field.value &&
      options.find(opt => Number(opt.value) !== Number(field.value))
    ) {
      return true
    } else {
      return false
    }
  })

  useEffect(() => {
    if (
      !showCustomField &&
      field.value &&
      // eslint-disable-next-line eqeqeq
      !options.find(opt => opt.value == field.value) // added no type check for bulk upload
    ) {
      setPillSet([
        ...options,
        ...(!isEmpty(field.value)
          ? [{ value: field.value, label: field.value }]
          : []),
      ])
    } else {
      setPillSet(options)
    }
  }, [field.value, options, showCustomField])

  const updateValue = (id: string | number) => {
    // eslint-disable-next-line eqeqeq
    const checkExists = field.value == id
    checkExists ? helpers.setValue(null) : helpers.setValue(id)
  }
  const setSelectorValue = (val: string) => {
    helpers.setValue(val)
  }

  useEffect(() => {
    if (showCustomField) {
      helpers.setValue(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCustomField])

  return (
    <div
      className={clsx(className, styles.selectField, styles.inputV2Wrap)}
      data-cy={name}
    >
      <label>
        <span className={clsx(styles.labelText, styles.inputLabelV2)}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
          {miniTooltip && (
            <Tooltip
              placement="top"
              title={() => (
                <div
                  dangerouslySetInnerHTML={{
                    __html: miniTooltip,
                  }}
                />
              )}
            >
              <TooltipInfo className={styles.tooltipPos} />
            </Tooltip>
          )}
        </span>
      </label>
      <div className={styles.optionsWrap}>
        {pillSet?.map((optionItem, index) => {
          // eslint-disable-next-line eqeqeq
          const isActive = optionItem.value == field.value
          return (
            <div
              role="menuitem"
              tabIndex={index}
              onKeyPress={() => {
                if (!disabled) {
                  setToggleCustomField(false)
                  updateValue(optionItem.value)
                }
              }}
              onClick={() => {
                if (!disabled) {
                  setToggleCustomField(false)
                  updateValue(optionItem.value)
                }
              }}
              key={index}
              className={clsx(styles.option, {
                [styles.optionActive]: isActive,
                [styles.optionDisabled]: disabled,
              })}
            >
              <span className={styles.optionText}>{optionItem.label}</span>
            </div>
          )
        })}
        {name !== 'sub_vertical_id' && (
          <div
            role="menuitem"
            tabIndex={options.length}
            onKeyPress={() =>
              !disabled && setToggleCustomField(showCustom => !showCustom)
            }
            onClick={() =>
              !disabled && setToggleCustomField(showCustom => !showCustom)
            }
            className={clsx(styles.option, {
              [styles.optionActive]: showCustomField,
              [styles.optionDisabled]: disabled,
            })}
          >
            <span className={styles.optionText}>I want to specify</span>
          </div>
        )}
      </div>
      {showCustomField && (
        <Input
          type={dataType}
          onChange={e => !disabled && setSelectorValue(e.target.value)}
          placeholder={placeHolder}
          defaultValue={field.value}
          className={clsx(styles.inputAreaV2, {
            [styles.borderRed]: meta.touched && meta.error,
            [styles.optionDisabled]: disabled,
          })}
        />
      )}
      {meta.touched && meta.error ? (
        <div className={styles.error}>
          <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
        </div>
      ) : null}
    </div>
  )
}

export default PillsSelector
